import { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Truck, MapPin } from 'lucide-react';

export default function DeliveryOptions({ postalCode, onDeliveryChange }) {
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [shippingCost, setShippingCost] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (deliveryType === 'delivery' && postalCode) {
      calculateShipping();
    } else if (deliveryType === 'pickup') {
      setShippingCost(0);
      onDeliveryChange({ type: 'pickup', cost: 0 });
    }
  }, [deliveryType, postalCode, onDeliveryChange]);

  const calculateShipping = async () => {
    if (!postalCode || postalCode.length < 4) {
      setShippingCost(0);
      onDeliveryChange({ type: 'delivery', cost: 0 });
      return;
    }

    setLoading(true);
    try {
      const response = await base44.functions.invoke('calculateShippingCost', {
        postalCode
      });
      
      const cost = response.data.shippingCost || 0;
      setShippingCost(cost);
      onDeliveryChange({ type: 'delivery', cost });
    } catch (error) {
      console.error('Error calculating shipping:', error);
      setShippingCost(0);
      onDeliveryChange({ type: 'delivery', cost: 0 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 border border-border p-5 rounded-sm bg-card">
      <h3 className="font-serif text-lg font-light text-foreground mb-4">Levering</h3>
      
      <div className="space-y-3">
        {/* Pickup Option */}
        <button
          type="button"
          onClick={() => setDeliveryType('pickup')}
          className={`w-full flex items-start gap-3 p-4 border-2 rounded-sm transition-all ${
            deliveryType === 'pickup'
              ? 'border-foreground bg-background'
              : 'border-border bg-background hover:border-border/60'
          }`}
        >
          <div className="flex-shrink-0">
            <MapPin size={20} className="text-foreground" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-serif text-base text-foreground font-medium">Hente selv</p>
            <p className="font-sans text-sm text-foreground/60">Gratis - Hent din badstue på vår lokasjon i Oslo</p>
          </div>
          <div className="text-right">
            <p className="font-serif text-sm text-foreground font-medium">Gratis</p>
          </div>
        </button>

        {/* Delivery Option */}
        <button
          type="button"
          onClick={() => setDeliveryType('delivery')}
          className={`w-full flex items-start gap-3 p-4 border-2 rounded-sm transition-all ${
            deliveryType === 'delivery'
              ? 'border-foreground bg-background'
              : 'border-border bg-background hover:border-border/60'
          }`}
        >
          <div className="flex-shrink-0">
            <Truck size={20} className="text-foreground" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-serif text-base text-foreground font-medium">Levering til adresse</p>
            <p className="font-sans text-sm text-foreground/60">
              {loading ? 'Beregner...' : postalCode && deliveryType === 'delivery' 
                ? `Levering til postnummer ${postalCode}`
                : 'Angir postnummer for pris'}
            </p>
          </div>
          <div className="text-right">
            <p className="font-serif text-sm text-foreground font-medium">
              {loading ? '...' : shippingCost > 0 ? `+${shippingCost.toLocaleString('nb-NO')} kr` : '-'}
            </p>
          </div>
        </button>
      </div>

      {deliveryType === 'delivery' && shippingCost > 0 && (
        <div className="mt-3 p-3 bg-background/50 rounded-sm border border-border/50">
          <p className="font-sans text-sm text-foreground/60">
            Leveringskost basert på postnummer {postalCode}
          </p>
        </div>
      )}
    </div>
  );
}